export const NOTIFICATIONS_KEY = '@aga_notifications';

// Browser event dispatched whenever the stored notification list changes, so
// open views (e.g. the Notifications page) can refresh live.
export const NOTIFICATIONS_UPDATED_EVENT = 'aga:notifications-updated';

function emitNotificationsUpdated() {
  try {
    window.dispatchEvent(new Event(NOTIFICATIONS_UPDATED_EVENT));
  } catch {
    /* non-browser / best effort */
  }
}

export type StoredNotification = {
  id: string;
  title: string;
  body: string;
  type: string;
  apkUrl?: string;
  route?: string;
  receivedAt: number;
  read: boolean;
};

function readRaw(): string | null {
  try {
    return localStorage.getItem(NOTIFICATIONS_KEY);
  } catch {
    return null;
  }
}

function writeRaw(value: string) {
  try {
    localStorage.setItem(NOTIFICATIONS_KEY, value);
  } catch {
    /* quota — best effort */
  }
}

function parseStoredNotifications(raw: string | null): StoredNotification[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export async function storeNotification(n: Omit<StoredNotification, 'id' | 'receivedAt' | 'read'>) {
  const existing = parseStoredNotifications(readRaw());
  const newEntry: StoredNotification = {
    ...n,
    title: String(n.title || 'SL Sweep'),
    body: String(n.body || ''),
    type: String(n.type || 'BROADCAST'),
    id: `${Date.now()}-${Math.random()}`,
    receivedAt: Date.now(),
    read: false,
  };
  const updated = [newEntry, ...existing].slice(0, 50);
  writeRaw(JSON.stringify(updated));
  emitNotificationsUpdated();
}

export async function getStoredNotifications(): Promise<StoredNotification[]> {
  return parseStoredNotifications(readRaw());
}

export async function markAllRead() {
  const existing = await getStoredNotifications();
  const updated = existing.map(n => ({ ...n, read: true }));
  writeRaw(JSON.stringify(updated));
  emitNotificationsUpdated();
}

export async function getUnreadCount(): Promise<number> {
  const existing = await getStoredNotifications();
  return existing.filter(n => !n.read).length;
}
